import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { CATALOG_FILTER_EXISTS, catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Link,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceled';

type RunStartResponse = {
  buildId: number;
  buildNumber: string | null;
  status: RunStatus;
  webUrl: string;
};

type RunStatusResponse = {
  buildId: number;
  status: RunStatus;
  rawStatus: string | null;
  result: string | null;
  queueTime: string | null;
  startTime: string | null;
  finishTime: string | null;
  webUrl: string;
};

type CatalogServiceOption = {
  name: string;
  namespace: string;
};

type PersistedReportsState = {
  selectedNamespace: string;
  selectedService: string;
  reportDate: string;
  startTime: string;
  endTime: string;
  run: RunStartResponse | null;
  runStatus: RunStatusResponse | null;
  error: string | null;
};

const REPORTS_STATE_SESSION_KEY = 'deuna.backstage.reports.state.v1';

const useStyles = makeStyles(theme => ({
  formCard: {
    marginBottom: theme.spacing(2),
  },
  markdownContainer: {
    '& img': {
      maxWidth: '100%',
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.background.default,
    },
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

function toLocalDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function humanStatus(status: RunStatus): string {
  if (status === 'queued') return 'Queued';
  if (status === 'running') return 'Running';
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  return 'Canceled';
}

function isInProgress(status: RunStatus | null): boolean {
  return status === 'queued' || status === 'running';
}

function isValidTime24h(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function markdownUrlTransform(url: string, key: string): string {
  if (key === 'src' && url.startsWith('data:image/')) {
    return url;
  }

  return defaultUrlTransform(url);
}

export function ReportsPage() {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [services, setServices] = useState<CatalogServiceOption[]>([]);
  const [selectedNamespace, setSelectedNamespace] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [reportDate, setReportDate] = useState(toLocalDateInputValue(new Date()));
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<RunStartResponse | null>(null);
  const [runStatus, setRunStatus] = useState<RunStatusResponse | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  const namespaces = useMemo(
    () => Array.from(new Set(services.map(service => service.namespace))).sort((a, b) => a.localeCompare(b)),
    [services],
  );

  const servicesForSelectedNamespace = useMemo(
    () => services
      .filter(service => service.namespace === selectedNamespace)
      .map(service => service.name)
      .sort((a, b) => a.localeCompare(b)),
    [services, selectedNamespace],
  );

  useEffect(() => {
    try {
      const rawState = sessionStorage.getItem(REPORTS_STATE_SESSION_KEY);
      if (!rawState) {
        return;
      }

      const parsed = JSON.parse(rawState) as Partial<PersistedReportsState>;

      if (typeof parsed.selectedNamespace === 'string') {
        setSelectedNamespace(parsed.selectedNamespace);
      }
      if (typeof parsed.selectedService === 'string') {
        setSelectedService(parsed.selectedService);
      }
      if (typeof parsed.reportDate === 'string') {
        setReportDate(parsed.reportDate);
      }
      if (typeof parsed.startTime === 'string') {
        setStartTime(parsed.startTime);
      }
      if (typeof parsed.endTime === 'string') {
        setEndTime(parsed.endTime);
      }
      if (parsed.run === null || typeof parsed.run === 'object') {
        setRun(parsed.run as RunStartResponse | null);
      }
      if (parsed.runStatus === null || typeof parsed.runStatus === 'object') {
        setRunStatus(parsed.runStatus as RunStatusResponse | null);
      }
      if (parsed.error === null || typeof parsed.error === 'string') {
        setError(parsed.error);
      }
    } catch {
      // ignore malformed storage state
    } finally {
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const state: PersistedReportsState = {
      selectedNamespace,
      selectedService,
      reportDate,
      startTime,
      endTime,
      run,
      runStatus,
      error,
    };

    try {
      sessionStorage.setItem(REPORTS_STATE_SESSION_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [isHydrated, selectedNamespace, selectedService, reportDate, startTime, endTime, run, runStatus, error]);

  useEffect(() => {
    let alive = true;

    const loadServices = async () => {
      setLoadingCatalog(true);
      setCatalogError(null);

      try {
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'microservice',
            'metadata.annotations.backstage.io/kubernetes-namespace': CATALOG_FILTER_EXISTS,
          },
          fields: ['metadata.name', 'metadata.annotations'],
        });

        const options: CatalogServiceOption[] = [];
        const seen = new Set<string>();

        for (const entity of response.items) {
          const name = entity.metadata.name?.trim();
          const namespace = entity.metadata.annotations?.['backstage.io/kubernetes-namespace']?.trim();

          if (!name || !namespace) {
            continue;
          }

          const key = `${namespace}/${name}`;
          if (seen.has(key)) {
            continue;
          }

          seen.add(key);
          options.push({ name, namespace });
        }

        options.sort((a, b) => {
          const byNamespace = a.namespace.localeCompare(b.namespace);
          if (byNamespace !== 0) {
            return byNamespace;
          }
          return a.name.localeCompare(b.name);
        });

        if (alive) {
          setServices(options);
        }
      } catch (e: any) {
        if (alive) {
          setServices([]);
          setCatalogError(e.message || 'Failed to load microservices from catalog');
        }
      } finally {
        if (alive) {
          setLoadingCatalog(false);
        }
      }
    };

    loadServices();

    return () => {
      alive = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    if (!isHydrated || loadingCatalog) {
      return;
    }

    if (!namespaces.length) {
      if (selectedNamespace !== '') setSelectedNamespace('');
      if (selectedService !== '') setSelectedService('');
      return;
    }

    if (!selectedNamespace || !namespaces.includes(selectedNamespace)) {
      setSelectedNamespace(namespaces[0]);
      return;
    }

    if (!servicesForSelectedNamespace.length) {
      if (selectedService !== '') setSelectedService('');
      return;
    }

    if (!selectedService || !servicesForSelectedNamespace.includes(selectedService)) {
      setSelectedService(servicesForSelectedNamespace[0]);
    }
  }, [isHydrated, loadingCatalog, namespaces, selectedNamespace, selectedService, servicesForSelectedNamespace]);

  const fetchStatus = useCallback(async (buildId: number) => {
    const baseUrl = await discoveryApi.getBaseUrl('reports-creator');
    const response = await fetchApi.fetch(`${baseUrl}/run/${buildId}`);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Failed to fetch run status (${response.status})`);
    }
    const data = (await response.json()) as RunStatusResponse;
    setRunStatus(data);
    return data;
  }, [discoveryApi, fetchApi]);

  const fetchReport = useCallback(async (buildId: number) => {
    setLoadingReport(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('reports-creator');
      const response = await fetchApi.fetch(`${baseUrl}/run/${buildId}/report`);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to load report (${response.status})`);
      }
      const data = (await response.json()) as { markdown: string };
      setMarkdown(data.markdown || '');
    } finally {
      setLoadingReport(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    if (!run) return undefined;

    let stopped = false;
    let intervalId = 0;

    const tick = async () => {
      try {
        const statusData = await fetchStatus(run.buildId);
        if (statusData.status === 'completed') {
          await fetchReport(run.buildId);
          window.clearInterval(intervalId);
          stopped = true;
        }
        if (statusData.status === 'failed' || statusData.status === 'canceled') {
          window.clearInterval(intervalId);
          stopped = true;
        }
      } catch (e: any) {
        setError(e.message || 'Failed to poll status');
        window.clearInterval(intervalId);
        stopped = true;
      }
    };

    intervalId = window.setInterval(() => {
      if (!stopped) tick();
    }, 10_000);

    tick();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchReport, fetchStatus, run]);

  useEffect(() => {
    if (!isHydrated || markdown || !run) {
      return;
    }

    const effectiveStatus = runStatus?.status || run.status;
    if (effectiveStatus !== 'completed') {
      return;
    }

    fetchReport(run.buildId).catch((e: any) => {
      setError(e.message || 'Failed to load report');
    });
  }, [fetchReport, isHydrated, markdown, run, runStatus]);

  let namespaceHelperText = 'Select namespace from catalog';
  if (loadingCatalog) {
    namespaceHelperText = 'Loading namespaces from catalog...';
  } else if (namespaces.length === 0) {
    namespaceHelperText = 'No namespaces available';
  }

  let serviceHelperText = 'Select microservice from catalog';
  if (!selectedNamespace) {
    serviceHelperText = 'Select a namespace first';
  } else if (servicesForSelectedNamespace.length === 0) {
    serviceHelperText = 'No microservices found for selected namespace';
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMarkdown('');
    setRun(null);
    setRunStatus(null);

    const normalizedStartTime = startTime.trim();
    const normalizedEndTime = endTime.trim();
    if (!isValidTime24h(normalizedStartTime) || !isValidTime24h(normalizedEndTime)) {
      setError('Start Time and End Time must use 24h format HH:MM (example: 00:00, 23:59).');
      return;
    }

    if (!selectedNamespace || !selectedService) {
      setError('Select a namespace and microservice from the catalog.');
      return;
    }

    setLoading(true);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('reports-creator');
      const response = await fetchApi.fetch(`${baseUrl}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceName: selectedService,
          serviceNamespace: selectedNamespace,
          reportDate,
          startTime: normalizedStartTime,
          endTime: normalizedEndTime,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to queue report (${response.status})`);
      }

      const data = (await response.json()) as RunStartResponse;
      setRun(data);
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const effectiveStatus = runStatus?.status || run?.status || null;

  return (
    <>
      <InfoCard title="Reports Creator" className={classes.formCard}>
        <form onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                select
                label="Namespace"
                value={selectedNamespace}
                onChange={event => setSelectedNamespace(event.target.value)}
                disabled={loadingCatalog || namespaces.length === 0}
                helperText={namespaceHelperText}
              >
                {namespaces.map(namespace => (
                  <MenuItem key={namespace} value={namespace}>
                    {namespace}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                select
                label="Service Name"
                value={selectedService}
                onChange={event => setSelectedService(event.target.value)}
                disabled={loadingCatalog || !selectedNamespace || servicesForSelectedNamespace.length === 0}
                helperText={serviceHelperText}
              >
                {servicesForSelectedNamespace.map(serviceName => (
                  <MenuItem key={serviceName} value={serviceName}>
                    {serviceName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Report Date"
                type="date"
                value={reportDate}
                onChange={event => setReportDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                label="Start Time"
                type="text"
                value={startTime}
                onChange={event => setStartTime(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '(?:[01]\\d|2[0-3]):[0-5]\\d' }}
                placeholder="00:00"
                helperText="Use 24h HH:MM (no AM/PM)"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                label="End Time"
                type="text"
                value={endTime}
                onChange={event => setEndTime(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '(?:[01]\\d|2[0-3]):[0-5]\\d' }}
                placeholder="23:59"
                helperText="Use 24h HH:MM (no AM/PM)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <div className={classes.actions}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Generate Report
                </Button>
                {loading && <Progress />}
              </div>
            </Grid>
          </Grid>
        </form>

        {catalogError && (
          <Typography color="error" style={{ marginTop: 12 }}>
            {catalogError}
          </Typography>
        )}

        {error && (
          <Typography color="error" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}

        {run && (
          <div style={{ marginTop: 16 }}>
            <Typography variant="body2">
              Build ID: <strong>{run.buildId}</strong>
            </Typography>
            <Typography variant="body2">
              Build Number: <strong>{run.buildNumber || 'N/A'}</strong>
            </Typography>
            <Typography variant="body2">
              Link: <Link href={run.webUrl} target="_blank" rel="noreferrer">Open in Azure DevOps</Link>
            </Typography>
          </div>
        )}
      </InfoCard>

      <InfoCard title="Run Status">
        {effectiveStatus ? (
          <div className={classes.statusRow}>
            <Chip label={humanStatus(effectiveStatus)} color={effectiveStatus === 'completed' ? 'primary' : 'default'} />
            {isInProgress(effectiveStatus) && (
              <>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">
                  Running pipeline...
                </Typography>
              </>
            )}
          </div>
        ) : (
          <Typography color="textSecondary">No run yet</Typography>
        )}

        {loadingReport && <Progress />}

        {markdown && !loadingReport && (
          <div className={classes.markdownContainer}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={markdownUrlTransform}>
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </InfoCard>
    </>
  );
}
